import { useEffect, useContext } from "react";
import { API_Version, ApiVersionInfo, ResultObj, allFetch, getControllerApiVersion } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { DateTimeFormate, defaultDateToDotNet6Format, normalizeDsId } from "../function";
import { CustomDate } from "../../utils/date/dateExtension";

/**產生apiManager實例，並透過factory概念，將產生好的東西放到Provider，往後要用就是從Provider拿出來用
 * @returns 
 */
export const useUserApi = () => {
    const apiManagerName = "userApiManager";
    const context = useContext(ApiContext);
    if (!!!context)
        throw new Error("userApiManager must be used within a ApiProvider");

    let { apiManagers, registerApiManager } = context;

    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newUserCareerRankApiManager = new UserApiManager();
            newUserCareerRankApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("ESD Error!", e);
            })
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<UserApiManager>(apiManagerName);
};

/** 實際上判斷現在是哪個apiManager的地方 */
export class UserApiManager implements UserApiInterface {
    private adapter: UserDotNet6Adapter | UserFrameWorkAdapter | null = null;
    private version: keyof typeof API_Version | undefined;
    public isReady: boolean = false;

    async init(): Promise<UserApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            // 沒有的話就嘗試取得api_version.json，取得不到就採用localstorage的預設API版本
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("User");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new UserDotNet6Adapter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new UserDotNet6Adapter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new UserFrameWorkAdapter();

        if (this.adapter) {
            this.UserSearch = this.adapter.UserSearch;
            this.UserSearchAll = this.adapter.UserSearchAll;
            this.UserSearchNoLevel = this.adapter.UserSearchNoLevel;
            this.UserSearchProxy = this.adapter.UserSearchProxy;
            this.UserSearchAllDealer = this.adapter.UserSearchAllDealer;
            this.UserExist = this.adapter.UserExist;
            this.UserCreate = this.adapter.UserCreate;
            this.UserDetail = this.adapter.UserDetail;
            this.UserUpdate = this.adapter.UserUpdate;
            this.UserUpdateDisable = this.adapter.UserUpdateDisable;
            this.UserResign = this.adapter.UserResign;
            this.ResetPassword = this.adapter.ResetPassword;
            this.UserUpdateLevel = this.adapter.UserUpdateLevel;
            this.UserUpdateDuty = this.adapter.UserUpdateDuty;
            this.UserUpdateCareerRank = this.adapter.UserUpdateCareerRank;
            this.UserLevelDutyHistorySearch = this.adapter.UserLevelDutyHistorySearch;
            this.UserCareerRankHistorySearch = this.adapter.UserCareerRankHistorySearch;
            this.UserRelatedCompanyRecordCreate = this.adapter.UserRelatedCompanyRecordCreate;
            this.UserRelatedCompanyRecordUpdate = this.adapter.UserRelatedCompanyRecordUpdate;
            this.UserRelatedCompanyRecordSearch = this.adapter.UserRelatedCompanyRecordSearch;
            this.isReady = true;
        }

        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: '遺失 UserAdapter，請先執行init()，初始化 UserAdapter', data: null });

    UserSearch = async (obj: UserSearchParams): Promise<ResultObj<UserSearchResItem[]>> => await this.emptyAdapter();
    UserSearchAll = async (obj: UserSearchAllParams): Promise<ResultObj<UserSearchAllResItem[]>> => await this.emptyAdapter();
    UserSearchNoLevel = async (obj: UserSearchNoLevelParams): Promise<ResultObj<UserSearchNoLevelResItem[]>> => await this.emptyAdapter();
    UserSearchProxy = async (obj: UserSearchProxyParams): Promise<ResultObj<UserSearchProxyResItem[]>> => await this.emptyAdapter();
    UserSearchAllDealer = async (obj: UserSearchAllDealerParams): Promise<ResultObj<UserSearchAllDealerResItem[]>> => await this.emptyAdapter();
    UserExist = async (obj: UserExistParams): Promise<ResultObj> => await this.emptyAdapter();
    UserCreate = async (obj: UserCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    UserDetail = async (obj: UserDetailParams): Promise<ResultObj<UserDetailResItem | null>> => await this.emptyAdapter();
    UserUpdate = async (obj: UserUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    UserUpdateDisable = async (obj: UserUpdateDisableParams): Promise<ResultObj> => await this.emptyAdapter();
    UserResign = async (obj: UserResignParams): Promise<ResultObj> => await this.emptyAdapter();
    ResetPassword = async (obj: ResetPasswordParams): Promise<ResultObj> => await this.emptyAdapter();
    UserUpdateLevel = async (obj: UserUpdateLevelParams): Promise<ResultObj> => await this.emptyAdapter();
    UserUpdateDuty = async (obj: UserUpdateDutyParams): Promise<ResultObj> => await this.emptyAdapter();
    UserUpdateCareerRank = async (obj: UserUpdateCareerRankParams): Promise<ResultObj> => await this.emptyAdapter();
    UserLevelDutyHistorySearch = async (obj: UserLevelDutyHistorySearchParams): Promise<ResultObj<UserLevelDutyHistorySearchResItem[]>> => await this.emptyAdapter();
    UserCareerRankHistorySearch = async (obj: UserCareerRankHistorySearchParams): Promise<ResultObj<UserCareerRankHistorySearchResItem[]>> => await this.emptyAdapter();
    UserRelatedCompanyRecordCreate = async (obj: UserRelatedCompanyRecordCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    UserRelatedCompanyRecordUpdate = async (obj: UserRelatedCompanyRecordUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    UserRelatedCompanyRecordSearch = async (obj: UserRelatedCompanyRecordSearchParams): Promise<ResultObj<UserRelatedCompanyRecordSearchResItem[]>> => await this.emptyAdapter();

}

/** 定義Meal controller的adapter需要有哪些function */
export interface UserApiInterface {
    /** @description 使用者查詢(同職位&以下的) */
    UserSearch: (obj: UserSearchParams) => Promise<ResultObj<UserSearchResItem[]>>;
    /** @description 使用者查詢(無視權限選所有在職的 請假選代理人用) */
    UserSearchAll: (obj: UserSearchAllParams) => Promise<ResultObj<UserSearchAllResItem[]>>;
    /** @description 使用者查詢(還沒被分配職務的) */
    UserSearchNoLevel: (obj: UserSearchNoLevelParams) => Promise<ResultObj<UserSearchNoLevelResItem[]>>;
    /** @description 使用者查詢(選代理人用的，上層一層+同職務+下層全部**排除自己) */
    UserSearchProxy: (obj: UserSearchProxyParams) => Promise<ResultObj<UserSearchProxyResItem[]>>;
    /** @description 使用者查詢(可以查全經銷商的帳號們) */
    UserSearchAllDealer: (obj: UserSearchAllDealerParams) => Promise<ResultObj<UserSearchAllDealerResItem[]>>;
    /** @description 使用者檢查是否存在 */
    UserExist: (obj: UserExistParams) => Promise<ResultObj>;
    /** @description 使用者建立 */
    UserCreate: (obj: UserCreateParams) => Promise<ResultObj>;
    /** @description 使用者細項 */
    UserDetail: (obj: UserDetailParams) => Promise<ResultObj<UserDetailResItem | null>>;
    /** @description 使用者修改 */
    UserUpdate: (obj: UserUpdateParams) => Promise<ResultObj>;
    /** @description 使用者停用開放 */
    UserUpdateDisable: (obj: UserUpdateDisableParams) => Promise<ResultObj>;
    /** @description 使用者離職 */
    UserResign: (obj: UserResignParams) => Promise<ResultObj>;
    /** @description 使用者重設密碼 */
    ResetPassword: (obj: ResetPasswordParams) => Promise<ResultObj>;
    /** @description 使用者修改職務 */
    UserUpdateLevel: (obj: UserUpdateLevelParams) => Promise<ResultObj>;
    /** @description 使用者更新職責 */
    UserUpdateDuty: (obj: UserUpdateDutyParams) => Promise<ResultObj>;
    /** @description 使用者修改職等職級 */
    UserUpdateCareerRank: (obj: UserUpdateCareerRankParams) => Promise<ResultObj>;
    /** @description 使用者職務職責異動紀錄查詢 */
    UserLevelDutyHistorySearch: (obj: UserLevelDutyHistorySearchParams) => Promise<ResultObj<UserLevelDutyHistorySearchResItem[]>>;
    /** @description 使用者職等職級異動紀錄查詢 */
    UserCareerRankHistorySearch: (obj: UserCareerRankHistorySearchParams) => Promise<ResultObj<UserCareerRankHistorySearchResItem[]>>;
    /** @description 新增使用者關係企業紀錄 */
    UserRelatedCompanyRecordCreate: (obj: UserRelatedCompanyRecordCreateParams) => Promise<ResultObj>;
    /** @description 修改使用者關係企業紀錄 */
    UserRelatedCompanyRecordUpdate: (obj: UserRelatedCompanyRecordUpdateParams) => Promise<ResultObj>;
    /** @description 查詢使用者關係企業紀錄 */
    UserRelatedCompanyRecordSearch: (obj: UserRelatedCompanyRecordSearchParams) => Promise<ResultObj<UserRelatedCompanyRecordSearchResItem[]>>;

}

class UserDotNet6Adapter implements UserApiInterface {
    UserSearch = async (obj: UserSearchParams): Promise<ResultObj<UserSearchResItem[]>> => await allFetch("User/UserSearch", obj);

    UserSearchAll = async (obj: UserSearchAllParams): Promise<ResultObj<UserSearchAllResItem[]>> => await allFetch("User/UserSearchAll", obj);

    UserSearchNoLevel = async (obj: UserSearchNoLevelParams): Promise<ResultObj<UserSearchNoLevelResItem[]>> => await allFetch("User/UserSearchNoLevel", obj);

    UserSearchProxy = async (obj: UserSearchProxyParams): Promise<ResultObj<UserSearchProxyResItem[]>> => await allFetch("User/UserSearchProxy", obj);

    UserSearchAllDealer = async (obj: UserSearchAllDealerParams): Promise<ResultObj<UserSearchAllDealerResItem[]>> => await allFetch("User/UserSearchAllDealer", {
        ...obj,
        ds_id: normalizeDsId(obj.ds_id)
    });

    UserExist = async (obj: UserExistParams): Promise<ResultObj> => await allFetch("User/UserExist", obj);

    UserCreate = async (obj: UserCreateParams): Promise<ResultObj> =>
        await allFetch("User/UserCreate", {
            ...obj,
            birthday: !!obj.birthday && obj.birthday.length > 0
                ? new Date(DateTimeFormate.dateFormate({ dateStr: obj.birthday, Mode: DateTimeFormate.DateMode.yyyyMMdd }))
                : null,
            on_board_date: !!obj.on_board_date && obj.on_board_date.length > 0
                ? new Date(DateTimeFormate.dateFormate({ dateStr: obj.on_board_date, Mode: DateTimeFormate.DateMode.yyyyMMdd }))
                : null,
        });

    UserDetail = async (obj: UserDetailParams): Promise<ResultObj<UserDetailResItem | null>> =>
        await allFetch("User/UserDetail", obj).then((res: ResultObj<UserDetailDotNet6ResItem | null>) => {
            return ({
                success: res.success,
                message: res.message,
                data: !!res.data
                    ? {
                        ...res.data,
                        cre_time: res.data.cre_time ? new CustomDate(res.data.cre_time).toYYYYMMDDHHMMSS() : '',
                        upd_time: res.data.upd_time ? new CustomDate(res.data.upd_time).toYYYYMMDDHHMMSS() : '',
                        birthday: res.data.birthday ? new CustomDate(res.data.birthday).toYYYYMMDD() : '',
                        on_board_date: res.data.on_board_date ? new CustomDate(res.data.on_board_date).toYYYYMMDD() : '',
                        signin_time: res.data.signin_time ? new CustomDate(res.data.signin_time).toYYYYMMDDHHMM() : '',
                        resign_date: res.data.resign_date ? new CustomDate(res.data.resign_date).toYYYYMMDD() : '',
                        SMT_username: res.data.SMT_username,
                        insurance_cancel_date: res.data.insurance_cancel_date ? new CustomDate(res.data.insurance_cancel_date).toYYYYMMDD() : '',
                    }
                    : res.data
            })
        });

    UserUpdate = async (obj: UserUpdateParams): Promise<ResultObj> => {
        return await allFetch("User/UserUpdate", {
            ...obj,
            birthday: obj.birthday && obj.birthday.length > 0
                ? new Date(DateTimeFormate.dateFormate({ dateStr: obj.birthday, Mode: DateTimeFormate.DateMode.yyyyMMdd }))
                : null,
            on_board_date: obj.on_board_date && obj.on_board_date.length > 0
                ? new Date(DateTimeFormate.dateFormate({ dateStr: obj.on_board_date, Mode: DateTimeFormate.DateMode.yyyyMMdd }))
                : null,
            insurance_cancel_date: obj.insurance_cancel_date && obj.insurance_cancel_date.length > 0
                ? new Date(DateTimeFormate.dateFormate({ dateStr: obj.insurance_cancel_date, Mode: DateTimeFormate.DateMode.yyyyMMdd }))
                : null,
        })
    };

    UserUpdateDisable = async (obj: UserUpdateDisableParams): Promise<ResultObj> => await allFetch("User/UserUpdateDisable", obj);

    UserResign = async (obj: UserResignParams): Promise<ResultObj> =>
        await allFetch("User/UserResign", {
            ...obj,
            resign_date: defaultDateToDotNet6Format(obj.resign_date),
            insurance_cancel_date: defaultDateToDotNet6Format(obj.insurance_cancel_date)
        });

    ResetPassword = async (obj: ResetPasswordParams): Promise<ResultObj> => await allFetch("User/ResetPassword", obj);

    UserUpdateLevel = async (obj: UserUpdateLevelParams): Promise<ResultObj> => await allFetch("User/UserUpdateLevel", obj);

    UserUpdateDuty = async (obj: UserUpdateDutyParams): Promise<ResultObj> => await allFetch("User/UserUpdateDuty", obj);

    UserUpdateCareerRank = async (obj: UserUpdateCareerRankParams): Promise<ResultObj> => await allFetch("User/UserUpdateCareerRank", obj);

    UserLevelDutyHistorySearch = async (obj: UserLevelDutyHistorySearchParams): Promise<ResultObj<UserLevelDutyHistorySearchResItem[]>> =>
        await allFetch("User/UserLevelDutyHistorySearch", {
            ...obj,
            date_end: obj.date_end ? new CustomDate(obj.date_end, true) : null,
            date_start: obj.date_start ? new CustomDate(obj.date_start, true) : null,
        }).then((res: ResultObj<UserLevelDutyHistorySearchDotNet6ResItem[] | any>) => ({
            success: res.success,
            message: res.message,
            page: res.page,
            data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                ? res.data.map((item: UserLevelDutyHistorySearchDotNet6ResItem) => ({
                    ...item,
                    cre_time: item.cre_time ? new CustomDate(item.cre_time).toYYYYMMDDHHMMSS() : '',
                    upd_time: item.upd_time ? new CustomDate(item.upd_time).toYYYYMMDDHHMMSS() : '',
                    date_start: item.date_start ? new CustomDate(item.date_start).toYYYYMMDD() : '',
                    date_end: item.date_end ? new CustomDate(item.date_end).toYYYYMMDD() : ''
                }))
                : res.data
        }));

    UserCareerRankHistorySearch = async (obj: UserCareerRankHistorySearchParams): Promise<ResultObj<UserCareerRankHistorySearchResItem[]>> =>
        await allFetch("User/UserCareerRankHistorySearch", {
            ...obj,
            date_end: obj.date_end ? new CustomDate(obj.date_end, true) : null,
            date_start: obj.date_start ? new CustomDate(obj.date_start, true) : null,
        }).then((res: ResultObj<UserCareerRankHistorySearchDotNet6ResItem[] | any>) => ({
            success: res.success,
            message: res.message,
            page: res.page,
            data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                ? res.data.map((item: UserCareerRankHistorySearchDotNet6ResItem) => ({
                    ...item,
                    cre_time: item.cre_time ? new CustomDate(item.cre_time).toYYYYMMDDHHMMSS() : '',
                    upd_time: item.upd_time
                        ? DateTimeFormate.dateFormate({ dateStr: item.upd_time, Mode: DateTimeFormate.DateMode.yyyyMMddhhmm, joinDate: '' })
                        : '',
                    date_start: item.date_start
                        ? DateTimeFormate.dateFormate({ dateStr: item.date_start, Mode: DateTimeFormate.DateMode.yyyyMMdd, joinDate: '' })
                        : '',
                    date_end: item.date_end
                        ? DateTimeFormate.dateFormate({ dateStr: item.date_end, Mode: DateTimeFormate.DateMode.yyyyMMdd, joinDate: '' })
                        : ''
                }))
                : res.data
        }));

    UserRelatedCompanyRecordCreate = async (obj: UserRelatedCompanyRecordCreateParams): Promise<ResultObj> =>
        await allFetch("User/UserRelatedCompanyRecordCreate", {
            ...obj,
            date_end: defaultDateToDotNet6Format(obj.date_end),
            date_start: defaultDateToDotNet6Format(obj.date_start),
        });

    UserRelatedCompanyRecordUpdate = async (obj: UserRelatedCompanyRecordUpdateParams): Promise<ResultObj> => await allFetch("User/UserRelatedCompanyRecordUpdate", obj);

    UserRelatedCompanyRecordSearch = async (obj: UserRelatedCompanyRecordSearchParams): Promise<ResultObj<UserRelatedCompanyRecordSearchResItem[]>> =>
        await allFetch("User/UserRelatedCompanyRecordSearch", obj).then((res: ResultObj<UserRelatedCompanyRecordSearchDotNet6ResItem[] | any>) => ({
            success: res.success,
            message: res.message,
            page: res.page,
            data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                ? res.data.map((item: UserRelatedCompanyRecordSearchDotNet6ResItem) => ({
                    ...item,
                    date_end: item.date_end
                        ? DateTimeFormate.dateFormate({ dateStr: item.date_end, Mode: DateTimeFormate.DateMode.yyyyMMdd })
                        : '',
                    date_start: item.date_start
                        ? DateTimeFormate.dateFormate({ dateStr: item.date_start, Mode: DateTimeFormate.DateMode.yyyyMMdd })
                        : '',
                }))
                : res.data
        }))


}

class UserFrameWorkAdapter implements UserApiInterface {
    UserSearch = async (obj: UserSearchParams): Promise<ResultObj<UserSearchResItem[]>> => await allFetch("User/UserSearch", obj);

    UserSearchAll = async (obj: UserSearchAllParams): Promise<ResultObj<UserSearchAllResItem[]>> => await allFetch("User/UserSearchAll", obj);

    UserSearchNoLevel = async (obj: UserSearchNoLevelParams): Promise<ResultObj<UserSearchNoLevelResItem[]>> => await allFetch("User/UserSearchNoLevel", obj);

    UserSearchProxy = async (obj: UserSearchProxyParams): Promise<ResultObj<UserSearchProxyResItem[]>> => await allFetch("User/UserSearchProxy", obj);

    /** Framework版本先直接指名UserSearchAll，理論上功能是一樣的 */
    UserSearchAllDealer = async (obj: UserSearchAllDealerParams): Promise<ResultObj<UserSearchAllDealerResItem[]>> =>
        await allFetch("User/UserSearchAll", {
            ...obj,
            ds_id: normalizeDsId(obj.ds_id)
        });

    UserExist = async (obj: UserExistParams): Promise<ResultObj> => await allFetch("User/UserExist", obj);

    UserCreate = async (obj: UserCreateParams): Promise<ResultObj> => await allFetch("User/UserCreate", obj);

    UserDetail = async (obj: UserDetailParams): Promise<ResultObj<UserDetailResItem | null>> =>
        await allFetch("User/UserDetail", obj).then((res: ResultObj) => {
            return ({
                success: res.success,
                message: res.message,
                data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                    ? res.data[0]
                    : res.data
            })
        });

    UserUpdate = async (obj: UserUpdateParams): Promise<ResultObj> => await allFetch("User/UserUpdate", obj);

    UserUpdateDisable = async (obj: UserUpdateDisableParams): Promise<ResultObj> => await allFetch("User/UserUpdateDisable", obj);

    UserResign = async (obj: UserResignParams): Promise<ResultObj> => await allFetch("User/UserResign", obj);

    ResetPassword = async (obj: ResetPasswordParams): Promise<ResultObj> => await allFetch("User/ResetPassword", obj);

    UserUpdateLevel = async (obj: UserUpdateLevelParams): Promise<ResultObj> => await allFetch("User/UserUpdateLevel", obj);

    UserUpdateDuty = async (obj: UserUpdateDutyParams): Promise<ResultObj> => await allFetch("User/UserUpdateDuty", obj);

    UserUpdateCareerRank = async (obj: UserUpdateCareerRankParams): Promise<ResultObj> => await allFetch("User/UserUpdateCareerRank", obj);

    UserLevelDutyHistorySearch = async (obj: UserLevelDutyHistorySearchParams): Promise<ResultObj<UserLevelDutyHistorySearchResItem[]>> => await allFetch("User/UserLevelDutyHistorySearch", obj);

    UserCareerRankHistorySearch = async (obj: UserCareerRankHistorySearchParams): Promise<ResultObj<UserCareerRankHistorySearchResItem[]>> => await allFetch("User/UserCareerRankHistorySearch", obj)

    UserRelatedCompanyRecordCreate = async (obj: UserRelatedCompanyRecordCreateParams): Promise<ResultObj> => await allFetch("User/UserRelatedCompanyRecordCreate", obj);

    UserRelatedCompanyRecordUpdate = async (obj: UserRelatedCompanyRecordUpdateParams): Promise<ResultObj> => await allFetch("User/UserRelatedCompanyRecordUpdate", obj);

    UserRelatedCompanyRecordSearch = async (obj: UserRelatedCompanyRecordSearchParams): Promise<ResultObj<UserRelatedCompanyRecordSearchResItem[]>> => await allFetch("User/UserRelatedCompanyRecordSearch", obj);

}


/**使用者查詢(同職位&以下的) (參數)
 * @description 使用者查詢(同職位&以下的) (參數)
 * @param {string} username 帳號
 * @param {string} name 姓名
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 *  * @param {string} disable 帳號狀態
 * @param {string} isresign 是否離職
 * @param {string} has_ul_id 是否有綁定職務
 * @param {string} ul_id 職務流水號
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface UserSearchParams {
    username?: string;
    name?: string;
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    disable?: string;
    isresign?: string;
    has_ul_id?: string;
    ul_id?: string;
    page?: number;
    num_per_page?: number;
}

/**@description 使用者查詢(同職位&以下的) (回傳) */
export interface UserSearchResItem {
    cre_time: string;
    disable: string;
    home_page_name: string;
    name: string;
    name_en: string;
    ul_name: string;
    user_id: string;
    username: string;
}

/**使用者查詢(無視權限選所有在職的 請假選代理人用) (參數)
 * @description 使用者查詢(無視權限選所有在職的 請假選代理人用) (參數)
 * @param {string} username 帳號
 * @param {string} name 姓名
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 * @param {string} ul_id 職務流水號
 * @param {string} ud_id 經銷商流水號
 * @param {string} has_ul_id 是否有綁定職務
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface UserSearchAllParams {
    username?: string;
    name?: string;
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    ul_id?: string;
    ud_id?: string;
    has_ul_id?: string;
    disable?: string;
    page?: number;
    num_per_page?: number;
}

/**@description 使用者查詢(無視權限選所有在職的 請假選代理人用) (回傳) */
export type UserSearchAllResItem = {
    cre_time: string;
    disable: string;
    name: string;
    ul_name: string;
    user_id: string;
    username: string;
    home_page_name: string;
    level_name?: string;
    ds_id: string; // 經銷商流水號
}

/**使用者查詢(還沒被分配職務的) (參數)
 * @description 使用者查詢(還沒被分配職務的) (參數)
 * @param {string} username 帳號
 * @param {string} name 姓名
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface UserSearchNoLevelParams {
    username?: string;
    name?: string;
    page?: number;
    num_per_page?: number;
}

/**@description 使用者查詢(還沒被分配職務的) (回傳) */
export interface UserSearchNoLevelResItem {
    cre_time: string; //TODO 跟凱文說要加
    disable: "N" | "Y";
    name: string;
    user_id: string;
    username: string;
    home_page_name: string;
}

/**@description 使用者查詢(選代理人用的，上層一層+同職務+下層全部**排除自己) (參數) */
export interface UserSearchProxyParams {
    user_id: string;
}

/**@description 使用者查詢(選代理人用的，上層一層+同職務+下層全部**排除自己) (回傳) */
export interface UserSearchProxyResItem {
    disable: string;
    home_page_name: string;
    name: string;
    ul_name: string;
    user_id: string;
    username: string;
}

/**@description 使用者查詢(可以查全經銷商的帳號們) (參數) */
export interface UserSearchAllDealerParams {
    ds_id: string,
    username?: string,
    name?: string,
    disable?: string,
    isresign?: string,
    has_ul_id?: string,
    ul_id?: string,
    ud_id?: string,
    page?: number,
    num_per_page?: number,
    ignore_level?: string
    excel?: string; // 是否為匯出Excel
}

/**@description 使用者查詢(可以查全經銷商的帳號們) (回傳) */
export interface UserSearchAllDealerResItem {
    disable: string,
    user_id: string,
    username: string,
    name: string,
    ul_id: string,
    ul_name: string,
    position_id: string,
    position_name: string,
    ds_id: string,
    ds_name: string
}

/**@description 使用者檢查是否存在 (參數) */
export interface UserExistParams {
    username: string;
}

/** 使用者建立 (參數)
 * @description 使用者建立 (參數)
 * @param {string} username 帳號
 * @param {string} name 名稱
 * @param {string} gender 性別
 * @param {string} birthday 生日
 * @param {string} telephone 電話
 * @param {string} mobile_phone 手機
 * @param {string} email 電子信箱
 * @param {string} city 縣市(聯絡)
 * @param {string} area 區域(聯絡)
 * @param {string} address 住址(聯絡)
 * @param {string} city_census 縣市(戶籍)
 * @param {string} area_census 區域(戶籍)
 * @param {string} address_census 住址(戶籍)
 * @param {string} on_board_date 到職日
 * @param {string} note 備註
 * @param {string} home_page 首頁
 * @param {string} blood_type 血型
 * @param {string} identity_card 身分證
 * @param {string} SMT_username SMT使用者帳號
 */
export interface UserCreateParams {
    username: string;
    password: string;
    name: string;
    gender?: string;
    birthday?: string;
    telephone?: string;
    mobile_phone?: string;
    email?: string;
    city?: string;
    area?: string;
    address?: string;
    city_census?: string;
    area_census?: string;
    address_census?: string;
    on_board_date: string;
    note?: string;
    home_page: string;
    blood_type?: string;
    identity_card?: string;
    SMT_username?: string;
}

/**@description 使用者細項 (參數) */
export interface UserDetailParams {
    user_id: string;
}

/**@description 使用者細項 (回傳) */
export interface UserDetailResItem {
    SMT_username?: string;
    address: string;
    address_census: string;
    area: string;
    area_census: string;
    birthday: string;
    blood_type: string;
    career_level: number;
    city: string;
    city_census: string;
    cre_time: string;
    disable: string;
    email: string;
    gender: string;
    home_page: string;
    identity_card: string;
    isresign: string;
    mobile_phone: string;
    name: string;
    note: string;
    on_board_date: string;
    position_id: string;
    resign_date: string;
    resign_reason: string;
    telephone: string;
    ucr_id: number;
    ul_id: number;
    ul_name: string;
    username: string;
    insurance_cancel_date?: string;
    ds_id: string;
}

/**@description DotNet6 使用者細項 (回傳) */
export interface UserDetailDotNet6ResItem {
    cre_userid: string;
    cre_time: string | null;
    upd_userid: string;
    upd_time: string | null;
    disable: string;
    company_code: string;
    user_id: string;
    username: string;
    password: string;
    name: string;
    gender: string;
    birthday: string | null;
    telephone: string;
    mobile_phone: string;
    email: string;
    city: string;
    area: string;
    address: string;
    city_census: string;
    area_census: string;
    address_census: string;
    on_board_date: string | null;
    note: string;
    home_page: string;
    signin_time: string | null;
    ul_id: number;
    blood_type: string;
    identity_card: string;
    ucr_id: number;
    career_level: number;
    isresign: string;
    resign_date: string | null;
    resign_reason: string;
    SMT_username: string;
    ul_name: string;
    home_page_name: string;
    position_id: string;
    ds_id: string;
    insurance_cancel_date: string | null;
}

/** 使用者修改 (參數)
 * @description 使用者修改 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} username 帳號
 * @param {string} name 名稱
 * @param {string} gender 性別
 * @param {string} birthday 生日
 * @param {string} telephone 電話
 * @param {string} mobile_phone 手機
 * @param {string} email 電子信箱
 * @param {string} city 縣市(聯絡)
 * @param {string} area 區域(聯絡)
 * @param {string} address 住址(聯絡)
 * @param {string} city_census 縣市(戶籍)
 * @param {string} area_census 區域(戶籍)
 * @param {string} address_census 住址(戶籍)
 * @param {string} on_board_date 到職日
 * @param {string} note 備註
 * @param {string} home_page 首頁
 * @param {string} blood_type 血型
 * @param {string} identity_card 身分證
 * @param {string} SMT_username SMT使用者帳號
 * @param {string} insurance_cancel_date 退保日期
 */
export interface UserUpdateParams {
    user_id: string;
    username: string;
    name: string;
    gender?: string;
    birthday?: string;
    telephone?: string;
    mobile_phone?: string;
    email?: string;
    city?: string;
    area?: string;
    address?: string;
    city_census?: string;
    area_census?: string;
    address_census?: string;
    on_board_date: string;
    note?: string;
    home_page: string;
    blood_type?: string;
    identity_card?: string;
    SMT_username?: string;
    insurance_cancel_date?: string;
}

/** 使用者停用開放 (參數)
 * @description 使用者停用開放 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} disable 是否禁用
 */
export interface UserUpdateDisableParams {
    user_id: string;
    disable: string;
}

/** 使用者離職 (參數)
 * @description 使用者離職 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} resign_date 離職日期
 * @param {string} resign_reason 離職原因
 * @param {string} insurance_cancel_date 退保日期
 */
export interface UserResignParams {
    user_id: string;
    resign_date: string;
    resign_reason?: string;
    insurance_cancel_date?: string;
}

/** 使用者重設密碼 (參數)
 * @description 使用者重設密碼 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} new_password 新密碼
 */
export interface ResetPasswordParams {
    user_id: string;
    new_password: string;
}

/** 使用者修改職務 (參數)
 * @description 使用者修改職務 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} ul_id 職務編號
 * @param {string} ucr_id 職等職級流水號
 * @param {string} career_level 職級
 */
export interface UserUpdateLevelParams {
    user_id: string;
    ul_id: number;
    ucr_id?: number;
    career_level?: number;
}

/** 使用者更新職責 (參數)
 * @description 使用者更新職責 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} ud_ids 要給跟不給的職責編號
 */
export interface UserUpdateDutyParams {
    user_id: string;
    ud_ids: { key: string, value: 'Y' | 'N' | '' }[];
}

/** 使用者修改職等職級 (參數)
 * @description 使用者修改職等職級 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} ucr_id 職等職級流水號
 * @param {string} career_level 職級
 */
export interface UserUpdateCareerRankParams {
    user_id: string;
    ucr_id?: number;
    career_level?: number;
}

/** 使用者職務職責異動紀錄查詢 (參數)
 * @description 使用者職務職責異動紀錄查詢 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 */
export interface UserLevelDutyHistorySearchParams {
    user_id: string;
    date_start?: string;
    date_end?: string;
}

/**@description 使用者職務職責異動紀錄查詢 (回傳) */
export interface UserLevelDutyHistorySearchResItem {
    cre_time: string;
    date_end: string;
    date_start: string;
    duty_json: string;
    note: string;
    ul_code: string;
    ul_id: number;
    ul_name: string;
    user_id: string;
    ds_id: string;
}

/**@description DotNet6 使用者職務職責異動紀錄查詢 (回傳) */
export interface UserLevelDutyHistorySearchDotNet6ResItem {
    cre_userid: string;
    cre_time: string | null;
    upd_userid: string;
    upd_time: string | null;
    uldh_id: number;
    user_id: string;
    date_start: string | null;
    date_end: string | null;
    ul_id: number;
    duty_json: string;
    note: string;
    ul_code: string;
    ul_name: string;
}

/** 使用者職等職級異動紀錄查詢 (參數)
 * @description 使用者職等職級異動紀錄查詢 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 */
export interface UserCareerRankHistorySearchParams {
    user_id: string;
    date_start?: string;
    date_end?: string;
}

/**@description 使用者職等職級異動紀錄查詢 (回傳) */
export interface UserCareerRankHistorySearchResItem {
    career_level: number;
    career_rank: number | string;
    cre_time: string;
    date_end: string;
    date_start: string;
    note: string;
    ucr_id: number;
    ucrh_id: number;
    user_id: string;
    ds_id: string;
}

/**@description DotNet6 使用者職等職級異動紀錄查詢 (回傳) */
export interface UserCareerRankHistorySearchDotNet6ResItem {
    cre_userid: string;
    cre_time: string | null;
    upd_userid: string;
    upd_time: string | null;
    ucrh_id: number;
    user_id: string;
    date_start: string | null;
    date_end: string | null;
    ucr_id: number;
    career_rank: number | string;
    career_level: number;
    note: string;
}

/** 新增使用者關係企業紀錄 (參數)
 * @description 新增使用者關係企業紀錄 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} name 企業名稱
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 */
export interface UserRelatedCompanyRecordCreateParams {
    user_id: string;
    name: string; // 企業名稱
    date_start: string;
    date_end: string;
}

/** 修改使用者關係企業紀錄 (參數)
 * @description 修改使用者關係企業紀錄 (參數)
 * @param {string} rcr_id 流水號
 * @param {string} visible 是否可見
 */
export interface UserRelatedCompanyRecordUpdateParams {
    rcr_id: number,
    visible: string
}

/** 查詢使用者關係企業紀錄 (參數)
 * @description 查詢使用者關係企業紀錄 (參數)
 * @param {string} user_id 帳號編號
 * @param {string} rcr_id 流水號
 * @param {string} visible 是否可見
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface UserRelatedCompanyRecordSearchParams {
    user_id?: string;
    rcr_id?: string;
    visible?: string;
    page?: number;
    num_per_page?: number;
}

/**@description 查詢使用者關係企業紀錄 (回傳) */
export interface UserRelatedCompanyRecordSearchResItem {
    name: string;
    user_id: string;
    rcr_id: number;
    visible: string;
    date_end: string;
    date_start: string;
}

/**@description DotNet6 查詢使用者關係企業紀錄 (回傳) */
export interface UserRelatedCompanyRecordSearchDotNet6ResItem {
    cre_userid: string;
    cre_time: string | null;
    upd_userid: string;
    upd_time: string | null;
    name: string;
    user_id: string;
    rcr_id: number;
    visible: string;
    date_end: string | null;
    date_start: string | null;
}

